import { z } from 'zod';

/**
 * Zod schemas for jsonb payloads crossing a trust boundary into the UI
 * (lineups / team stats stored raw from the provider, score breakdowns,
 * lineup snapshots). Parse, never assume.
 */

export const lineupPlayerSchema = z.object({
  player: z.object({
    id: z.number().nullish(),
    name: z.string().nullish(),
    number: z.number().nullish(),
    pos: z.string().nullish(),
    grid: z.string().nullish(),
  }),
});

export const lineupSchema = z.object({
  team: z.object({
    id: z.number().nullish(),
    name: z.string().nullish(),
    logo: z.string().nullish(),
  }),
  coach: z
    .object({ id: z.number().nullish(), name: z.string().nullish(), photo: z.string().nullish() })
    .nullish(),
  formation: z.string().nullish(),
  startXI: z.array(lineupPlayerSchema).nullish(),
  substitutes: z.array(lineupPlayerSchema).nullish(),
});

export const lineupsPayloadSchema = z.array(lineupSchema);
export type LineupPayload = z.infer<typeof lineupSchema>;

export const teamStatsSchema = z.object({
  team: z.object({
    id: z.number().nullish(),
    name: z.string().nullish(),
    logo: z.string().nullish(),
  }),
  statistics: z
    .array(
      z.object({
        type: z.string(),
        value: z.union([z.number(), z.string()]).nullable(),
      }),
    )
    .nullish(),
});

export const teamStatsPayloadSchema = z.array(teamStatsSchema);
export type TeamStatsPayload = z.infer<typeof teamStatsSchema>;

/** fixtures.injuries jsonb: per-side absence lists (injuries / suspensions). */
export const injuryItemSchema = z.object({
  name: z.string(),
  photo: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  reason: z.string(),
});
export const injuriesSchema = z.object({
  home: z.array(injuryItemSchema),
  away: z.array(injuryItemSchema),
});
export type ParsedInjuries = z.infer<typeof injuriesSchema>;

export const breakdownEntrySchema = z.object({
  reason: z.string(),
  value: z.number(),
  points: z.number(),
});

export const breakdownSchema = z.array(breakdownEntrySchema);
export type ParsedBreakdown = z.infer<typeof breakdownSchema>;

export const snapshotPickSchema = z.object({
  player_id: z.string(),
  position: z.enum(['GK', 'DEF', 'MID', 'FWD']),
  is_starting: z.boolean(),
  is_captain: z.boolean(),
  is_vice: z.boolean(),
  slot: z.number(),
  price: z.number().nullish(),
});

export const snapshotSchema = z.array(snapshotPickSchema);
export type ParsedSnapshot = z.infer<typeof snapshotSchema>;

/** Payload of the get_public_squad RPC (another manager's revealed lineup). */
export const publicSquadPickSchema = z.object({
  player_id: z.string(),
  name: z.string(),
  known_as: z.string().nullish(),
  position: z.enum(['GK', 'DEF', 'MID', 'FWD']),
  team_name: z.string().nullish(),
  flag_url: z.string().nullish(),
  is_starting: z.boolean(),
  is_captain: z.boolean(),
  is_vice: z.boolean(),
  slot: z.number(),
  points: z.number().nullish(),
});

export const publicSquadSchema = z.object({
  team: z.object({
    id: z.string(),
    name: z.string(),
    manager: z.string(),
    avatar_url: z.string().nullish(),
    total_points: z.number(),
    favorite_team_name: z.string().nullish(),
    favorite_team_logo: z.string().nullish(),
    // null for an unauthenticated caller (auth.uid() is null); a real boolean
    // for any signed-in user, which is the only path the app uses.
    is_owner: z.boolean().nullish(),
  }),
  gameweek: z
    .object({
      id: z.string(),
      number: z.number(),
      round_label: z.string(),
      status: z.enum(['upcoming', 'live', 'finished']),
    })
    .nullable(),
  picks: z.array(publicSquadPickSchema).nullable(),
});

export type PublicSquad = z.infer<typeof publicSquadSchema>;
export type PublicSquadPick = z.infer<typeof publicSquadPickSchema>;

/**
 * Match-center map payloads (get_match_xg) crossing the same trust boundary as
 * lineups / team stats: the average-position dots and the shotmap dots are
 * plotted DIRECTLY from provider x/y/xg, so a malformed or renamed numeric field
 * would otherwise render a dot at NaN → the card corner. These guard the numeric
 * coordinates (finite, non-NaN) so a bad row drops and a wholly bad slice
 * self-hides, mirroring how lineups/team_stats already parse before they render.
 */
const finiteNumber = z.number().finite();

/** One average-position dot: numeric x/y are REQUIRED + finite (they place the
 *  dot); everything else is descriptive and tolerated as nullish. */
export const averagePositionPlayerSchema = z.object({
  n: z.number().nullish().transform((v) => v ?? 0),
  x: finiteNumber,
  y: finiteNumber,
  pos: z.string().nullish().transform((v) => v ?? null),
  name: z.string().nullish().transform((v) => v ?? ''),
  player_id: z.number().nullish().transform((v) => v ?? null),
});

export const averagePositionsSchema = z.object({
  home: z.array(averagePositionPlayerSchema),
  away: z.array(averagePositionPlayerSchema),
});
export type ParsedAveragePositions = z.infer<typeof averagePositionsSchema>;

/** One shotmap dot: x/y/xg are REQUIRED + finite (they place + size the dot);
 *  the optional goal-mouth coords are guarded but nullable (only on-frame shots
 *  carry them). Outcome flags default to false so a missing flag never throws. */
export const matchShotSchema = z.object({
  team_side: z.enum(['home', 'away']),
  player_name: z.string().nullish().transform((v) => v ?? ''),
  minute: z.number().nullish().transform((v) => v ?? 0),
  x: finiteNumber,
  y: finiteNumber,
  xg: finiteNumber.nullish().transform((v) => v ?? 0),
  result: z.string().nullish().transform((v) => v ?? ''),
  situation: z.string().nullish().transform((v) => v ?? ''),
  body_part: z.string().nullish().transform((v) => v ?? ''),
  is_goal: z.boolean().nullish().transform((v) => v ?? false),
  is_on_target: z.boolean().nullish().transform((v) => v ?? false),
  is_headed: z.boolean().nullish().transform((v) => v ?? false),
  is_penalty: z.boolean().nullish().transform((v) => v ?? false),
  is_outside_box: z.boolean().nullish().transform((v) => v ?? false),
  is_blocked: z.boolean().nullish().transform((v) => v ?? false),
  gm_x: finiteNumber.nullish().transform((v) => v ?? null),
  gm_y: finiteNumber.nullish().transform((v) => v ?? null),
  gm_z: finiteNumber.nullish().transform((v) => v ?? null),
  goal_mouth_location: z.string().nullish().transform((v) => v ?? null),
  block_x: finiteNumber.nullish().transform((v) => v ?? null),
  block_y: finiteNumber.nullish().transform((v) => v ?? null),
  block_z: finiteNumber.nullish().transform((v) => v ?? null),
});

export const matchShotsSchema = z.array(matchShotSchema);
export type ParsedMatchShot = z.infer<typeof matchShotSchema>;

/** Safe parse helper: returns null instead of throwing for display-only data. */
export function parseOrNull<T>(schema: z.ZodType<T>, value: unknown): T | null {
  const result = schema.safeParse(value);
  return result.success ? result.data : null;
}
